import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EvaluationModeCard = ({ onSelect, disabled = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  const evaluationFeatures = [
    {
      icon: 'TrendingUp',
      title: 'Progress Tracking',
      description: 'View your fitness journey over time'
    },
    {
      icon: 'BarChart3',
      title: 'Performance Analytics',
      description: 'Detailed exercise performance data'
    },
    {
      icon: 'Target',
      title: 'Goal Achievement',
      description: 'Track your fitness milestones'
    },
    {
      icon: 'Calendar',
      title: 'Session History',
      description: 'Review past training sessions'
    }
  ];

  const handleCardClick = () => {
    if (!disabled && onSelect) {
      onSelect();
    }
  };

  return (
    <div
      className={`
        bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-primary/20 
        rounded-2xl p-8 transition-all duration-300 ease-out cursor-pointer 
        touch-spacing-lg breathing-focus hover:shadow-lg
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${isHovered && !disabled ? 'transform scale-105 border-primary/40' : ''}
      `}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Access evaluation mode"
      aria-disabled={disabled}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="BarChart3" size={48} className="text-primary" />
        </div>
        <h3 className="text-3xl font-bold text-foreground mb-2">
          Evaluation Mode
        </h3>
        <p className="text-lg text-muted-foreground">
          Review your progress and performance data
        </p>
      </div>
      {/* Features Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        {evaluationFeatures?.map((feature, index) => (
          <div 
            key={index}
            className="flex items-start space-x-4 p-4 bg-surface/50 rounded-xl border border-border/50"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name={feature?.icon} size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-foreground mb-1">
                {feature?.title}
              </h4>
              <p className="text-sm text-muted-foreground">
                {feature?.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      {/* Stats Preview */}
      <div className="bg-surface/70 rounded-xl p-6 mb-6 border border-border/50">
        <h4 className="text-xl font-semibold text-foreground mb-4 text-center">
          Your Recent Activity
        </h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary">12</p>
            <p className="text-sm text-muted-foreground">Sessions</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary">85%</p>
            <p className="text-sm text-muted-foreground">Completion</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-success">+15%</p>
            <p className="text-sm text-muted-foreground">Improvement</p>
          </div>
        </div>
      </div>
      {/* Action Button */}
      <Button
        variant="outline"
        size="xl"
        fullWidth
        iconName="BarChart3"
        iconPosition="left"
        className="text-xl py-4 border-primary/30 hover:bg-primary/5"
        disabled={disabled}
      >
        View My Progress
      </Button>
      {/* Info Badge */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm">
          <Icon name="Info" size={16} />
          <span>Track your fitness journey</span>
        </span>
      </div>
    </div>
  );
};

export default EvaluationModeCard;